import { Alert, Box, Stack, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type { Span, SpanStatisticsBaselineWindow } from "../api/client";
import { Panel } from "../components/layout/Panel";
import { PageErrorState } from "../components/pages/PageErrorState";
import { CompGrid, type Column } from "../components/grid/CompGrid";
import { DurationComparison } from "../components/stats/DurationComparison";
import { SpanHierarchy } from "../components/spans/SpanHierarchy";
import { BaselineSelector } from "../components/filters/BaselineSelector";
import { FilterBar } from "../components/filters/FilterBar";
import { JsonTree } from "../components/common/JsonTree";
import { useLogs } from "../hooks/useLogs";
import { useSpan } from "../hooks/useSpan";
import { useSpanStatistics } from "../hooks/useSpanStatistics";
import { useTrace } from "../hooks/useTrace";
import type { TimeRange } from "../utils/time";
import {
  formatBaselineWindow,
  formatDuration,
  formatLogMessage,
  formatSpanKind,
  formatStatusCode,
} from "../utils/formatting";
import type { LogRecord } from "../api/client";
import { IdLink, SpanIdLink, TraceIdLink } from "../components/links/IdLink";
import { useHierarchyContext } from "../contexts/HierarchyContext";

export function PageSingleSpanDetail() {
  const navigate = useNavigate();
  const { hierarchy } = useHierarchyContext();
  const serviceSlug = hierarchy?.service?.slug ?? null;
  const { spanId } = useParams<{ spanId: string }>();

  const [baseline, setBaseline] = useState<SpanStatisticsBaselineWindow>("24h");

  const {
    span,
    loading: spanLoading,
    error: spanError,
  } = useSpan({
    serviceSlug,
    spanId: spanId ?? null,
    enabled: !!serviceSlug && !!spanId,
  });
  const durationMs = useMemo(() => {
    if (!span) return null;
    return new Date(span.end_time).getTime() - new Date(span.start_time).getTime();
  }, [span]);

  // Create time range for statistics using span's start time as the baseline reference
  // The baseline will be calculated as [span.start_time - baseline_duration, span.start_time]
  // We extend the end time a bit to include more data for comparison
  const statisticsTimeRange = useMemo<TimeRange | null>(() => {
    if (!span) return null;
    const spanStart = new Date(span.start_time);
    const spanEnd = new Date(span.end_time);
    // Use span's start time as the start (baseline reference point)
    // Extend end time by 1 hour to include more data for statistics
    const rangeEnd = new Date(spanEnd.getTime() + 60 * 60 * 1000); // 1 hour after span end
    return { from: spanStart, to: rangeEnd };
  }, [span]);

  const {
    trace,
    loading: traceLoading,
    error: traceError,
  } = useTrace({
    serviceSlug,
    traceId: span?.trace_id ?? null,
    enabled: !!serviceSlug && !!span?.trace_id,
  });

  const {
    stats,
    loading: statsLoading,
    error: statsError,
  } = useSpanStatistics({
    serviceSlug,
    timeRange: statisticsTimeRange ?? { from: new Date(), to: new Date() },
    serviceName: span?.service_name,
    serviceInstance: span?.service_instance ?? undefined,
    spanName: span?.span_name,
    baseline,
    enabled: !!serviceSlug && !!span && !!statisticsTimeRange,
    limit: 10,
  });
  const stat = stats[0];

  const {
    logs,
    loading: logsLoading,
    error: logsError,
  } = useLogs({
    serviceSlug,
    spanIds: spanId ? [spanId] : [],
    enabled: !!serviceSlug && !!spanId,
    limit: 200,
  });

  const loading = spanLoading || traceLoading || statsLoading || logsLoading;
  const error = spanError || traceError || statsError || logsError;

  const handleNavigateSpan = (targetSpanId: string) => {
    if (!serviceSlug) return;
    // Navigate without time range since span lookup doesn't need it
    navigate(
      `/services/${encodeURIComponent(serviceSlug)}/spans/${encodeURIComponent(targetSpanId)}`,
    );
  };

  const logColumns: Column<LogRecord>[] = [
    {
      id: "timestamp",
      label: "Time",
      sortable: true,
      sortValue: (log) => new Date(log.ts).getTime(),
      render: (log) => <Typography variant="body2">{new Date(log.ts).toLocaleString()}</Typography>,
    },
    {
      id: "severity",
      label: "Severity",
      sortable: true,
      sortValue: (log) => log.severity_text || "",
      render: (log) => <Typography variant="body2">{log.severity_text}</Typography>,
    },
    {
      id: "body",
      label: "Message",
      sortable: true,
      sortValue: (log) => log.body,
      render: (log) => <Typography variant="body2">{formatLogMessage(log.body, log.log_attrs)}</Typography>,
    },
    {
      id: "attrs",
      label: "Attributes",
      sortable: false,
      render: (log) => (
        <Typography variant="body2" className="om-text-mono" sx={{ opacity: 0.7 }}>
          {Object.keys(log.log_attrs ?? {}).length > 0 ? "{…}" : "-"}
        </Typography>
      ),
    },
  ];

  if (!serviceSlug) {
    return (
      <Box>
        <Alert severity="warning">
          No Service in URL. Please navigate from a product’s services list.
        </Alert>
      </Box>
    );
  }

  if (!spanId) {
    return (
      <Box>
        <PageErrorState message="Missing spanId in URL." />
      </Box>
    );
  }

  if (!loading && !span) {
    return (
      <Box>
        <PageErrorState message="Span not found." />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3, width: "100%" }}>
      <Panel>
        <FilterBar>
          <BaselineSelector value={baseline} onChange={setBaseline} />
        </FilterBar>
      </Panel>

      {span && stat && durationMs != null ? (
        <Panel title="Duration analysis" subtitle="Compared to baseline">
          <Alert severity="info" sx={{ mb: 1.5 }}>
            Baseline is the window ending at this span&apos;s start time ({formatBaselineWindow(baseline)} before span start).
          </Alert>
          <Stack spacing={1.5}>
            {stat.baseline ? (
              <DurationComparison
                durationMs={durationMs}
                p50Ms={stat.baseline.p50_duration}
                p90Ms={stat.baseline.p90_duration}
                p95Ms={stat.baseline.p95_duration}
              />
            ) : (
              <Typography variant="body2" sx={{ opacity: 0.7 }}>
                No baseline statistics available for this span.
              </Typography>
            )}
          </Stack>
        </Panel>
      ) : (
        <Panel title="Duration analysis">
          <Typography variant="body2" sx={{ opacity: 0.7 }}>
            No percentile statistics available for this span.
          </Typography>
        </Panel>
      )}

      
{span ? (
        <Panel title="Overview">
          <Stack spacing={1}>
            <Stack direction="row" spacing={2} sx={{ flexWrap: "wrap" }}>
              <Typography variant="body2">
                <b>Span ID:</b> <span className="om-text-mono">{span.span_id}</span>
              </Typography>
              <Typography variant="body2">
                <b>Trace ID:</b>{" "}
              </Typography>
                <TraceIdLink serviceSlug={serviceSlug} traceId={span.trace_id} truncateAt={0} />
              <Typography variant="body2">
                <b>Parent:</b>{" "}
              </Typography>
                <SpanIdLink
                  serviceSlug={serviceSlug}
                  spanId={span.parent_span_id ?? null}
                  truncateAt={0}
                />
              <Typography variant="body2">
                <b>Performance:</b>
              </Typography>
              <IdLink to={`/services/${encodeURIComponent(serviceSlug)}/spans/perf/${encodeURIComponent(span.span_name)}`} value={span.span_name} truncateAt={0} />
            </Stack>

            <Stack direction="row" spacing={2} sx={{ flexWrap: "wrap", opacity: 0.85 }}>
              <Typography variant="body2">
                <b>Service:</b> {span.service_name}
                {span.service_instance ? ` (${span.service_instance})` : ""}
              </Typography>
              <Typography variant="body2">
                <b>Kind:</b> {formatSpanKind(span.kind)}
              </Typography>
              <Typography variant="body2">
                <b>Status:</b> {formatStatusCode(span.status_code)}
                {span.status_message ? ` (${span.status_message})` : ""}
              </Typography>
              <Typography variant="body2">
                <b>Events:</b> {span.events_count}
              </Typography>
            </Stack>

            <Stack direction="row" spacing={2} sx={{ flexWrap: "wrap", opacity: 0.85 }}>
              <Typography variant="body2">
                <b>Start:</b> {new Date(span.start_time).toLocaleString()}
              </Typography>
              <Typography variant="body2">
                <b>End:</b> {new Date(span.end_time).toLocaleString()}
              </Typography>
              <Typography variant="body2">
                <b>Duration:</b> {durationMs != null ? formatDuration(durationMs) : "-"}
              </Typography>
            </Stack>
          </Stack>
        </Panel>
      ) : null}

      <Panel title="Span hierarchy (trace context)">
        {trace ? (
          <SpanHierarchy
            spans={trace.spans}
            currentSpanId={spanId}
            onNavigateSpan={handleNavigateSpan}
          />
        ) : (
          <Typography variant="body2" sx={{ opacity: 0.7 }}>
            Trace not loaded yet.
          </Typography>
        )}
      </Panel>

      <Panel title="Related logs" subtitle="Filtered to this span">
        <CompGrid
          items={logs}
          loading={logsLoading}
          error={logsError}
          emptyMessage="No logs associated with this span"
          keyExtractor={(log, index) => `${log.ts}-${index}`}
          columns={logColumns}
        />
      </Panel>

      {span ? (
        <Panel title="Span attributes">
          <JsonTree value={span.span_attrs} />
        </Panel>
      ) : null}

      {span ? (
        <Panel title="Resource attributes">
          <JsonTree value={span.resource_attrs} />
        </Panel>
      ) : null}
    </Box>
  );
}
